/**
 * セット商品一覧 + 編集ダイアログ (Phase B)。
 *
 * 「セット商品」は複数SKUを束ねたもの。一覧 + ダイアログで完結 (専用詳細ページなし)。
 */
import type { ProductAudienceKind } from '@goenhub/shared';
import { Edit, Package, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import {
  useDeleteProductBundle,
  useProductBundles,
  useUpsertProductBundle,
  type ProductBundleRecord,
} from '@/hooks/useProductBundles';
import { useProducts, type ProductRecord } from '@/hooks/useProducts';

const AUDIENCE_LABEL: Record<ProductAudienceKind, string> = {
  btob: 'BtoB',
  btoc: 'BtoC',
  both: '両方',
};

function fmtDate(v: unknown): string {
  if (!v) return '—';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '—';
}

function tsToInputDate(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    const d = (v as { toDate: () => Date }).toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
}

interface BundleEditState {
  open: boolean;
  existing: ProductBundleRecord | null;
}

export function ProductBundlesListPage() {
  const { data, isLoading } = useProductBundles();
  const { data: products } = useProducts();
  const upsert = useUpsertProductBundle();
  const del = useDeleteProductBundle();
  const [edit, setEdit] = useState<BundleEditState>({ open: false, existing: null });

  const handleDelete = async (b: ProductBundleRecord) => {
    if (!window.confirm(`セット「${b.name}」を削除します。`)) return;
    try {
      await del.mutateAsync(b.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Screen
      title="セット商品"
      description="複数 SKU を束ねたセット商品。見積でセット価格としてまとめて投入できます。"
      actions={
        <Button
          type="button"
          variant="primary"
          onClick={() => setEdit({ open: true, existing: null })}
        >
          <Plus />
          セットを追加
        </Button>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Package}
          title="セット商品はまだありません"
          description="複数の商品をセットで販売する場合に作成します。"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="py-2 px-3">セット名</th>
                <th className="py-2 px-3">構成</th>
                <th className="py-2 px-3 text-right">セット価格</th>
                <th className="py-2 px-3">有効期間</th>
                <th className="py-2 px-3">対象</th>
                <th className="py-2 px-3">状態</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)]"
                >
                  <td className="py-2 px-3 font-medium">{b.name}</td>
                  <td className="py-2 px-3 text-xs">
                    {b.items.length} 品目 (
                    {b.items.reduce((s, x) => s + x.quantity, 0)} 点)
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    ¥{b.bundlePriceIncludingTax.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {fmtDate(b.validFrom)} 〜 {fmtDate(b.validUntil)}
                  </td>
                  <td className="py-2 px-3">{AUDIENCE_LABEL[b.audienceKind]}</td>
                  <td className="py-2 px-3">
                    <Badge>{b.isActive ? '有効' : '停止'}</Badge>
                  </td>
                  <td className="py-2 px-3 space-x-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEdit({ open: true, existing: b })}
                    >
                      <Edit />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(b)}
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BundleEditDialog
        state={edit}
        products={products ?? []}
        onClose={() => setEdit({ open: false, existing: null })}
        onSave={async (input) => {
          try {
            await upsert.mutateAsync(input);
            toast.success('保存しました');
            setEdit({ open: false, existing: null });
          } catch (err) {
            toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
          }
        }}
      />
    </Screen>
  );
}

function BundleEditDialog({
  state,
  products,
  onClose,
  onSave,
}: {
  state: BundleEditState;
  products: ProductRecord[];
  onClose: () => void;
  onSave: (input: {
    id?: string;
    name: string;
    description?: string | null;
    items: Array<{ productId: string; quantity: number }>;
    bundlePriceExcludingTax: number;
    bundlePriceIncludingTax: number;
    validFrom: string;
    validUntil?: string | null;
    category: string;
    audienceKind: ProductAudienceKind;
    isActive: boolean;
  }) => Promise<void>;
}) {
  const { open, existing } = state;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [audienceKind, setAudienceKind] = useState<ProductAudienceKind>('btob');
  const [isActive, setIsActive] = useState(true);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [excTax, setExcTax] = useState(0);
  const [incTax, setIncTax] = useState(0);
  const [items, setItems] = useState<Array<{ productId: string; quantity: number }>>([]);

  useEffect(() => {
    if (open && existing) {
      setName(existing.name);
      setDescription(existing.description ?? '');
      setCategory(existing.category);
      setAudienceKind(existing.audienceKind);
      setIsActive(existing.isActive);
      setValidFrom(tsToInputDate(existing.validFrom));
      setValidUntil(tsToInputDate(existing.validUntil));
      setExcTax(existing.bundlePriceExcludingTax);
      setIncTax(existing.bundlePriceIncludingTax);
      setItems(existing.items);
    } else if (open) {
      setName('');
      setDescription('');
      setCategory('');
      setAudienceKind('btob');
      setIsActive(true);
      setValidFrom(new Date().toISOString().slice(0, 10));
      setValidUntil('');
      setExcTax(0);
      setIncTax(0);
      setItems([]);
    }
  }, [open, existing]);

  const productMap = useMemo(() => {
    const m = new Map<string, ProductRecord>();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  const addItem = () =>
    setItems((prev) => [...prev, { productId: '', quantity: 1 }]);
  const removeItem = (i: number) =>
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  const updateItem = (
    i: number,
    patch: Partial<{ productId: string; quantity: number }>,
  ) =>
    setItems((prev) =>
      prev.map((x, idx) => (idx === i ? { ...x, ...patch } : x)),
    );

  const handleSubmit = async () => {
    if (!name.trim() || !category.trim()) {
      toast.error('セット名・カテゴリは必須');
      return;
    }
    if (items.length < 2 || items.some((x) => !x.productId)) {
      toast.error('構成は2品目以上、商品選択必須');
      return;
    }
    await onSave({
      id: existing?.id,
      name: name.trim(),
      description: description.trim() || null,
      items,
      bundlePriceExcludingTax: Number(excTax) || 0,
      bundlePriceIncludingTax: Number(incTax) || 0,
      validFrom,
      validUntil: validUntil || null,
      category: category.trim(),
      audienceKind,
      isActive,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existing ? 'セット商品を編集' : 'セット商品を追加'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>セット名 *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>カテゴリ *</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} />
            </div>
            <div>
              <Label>対象</Label>
              <select
                value={audienceKind}
                onChange={(e) => setAudienceKind(e.target.value as ProductAudienceKind)}
                className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
              >
                <option value="btob">BtoB</option>
                <option value="btoc">BtoC</option>
                <option value="both">両方</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <Label className="!mb-0">有効</Label>
            </div>
            <div>
              <Label>有効開始</Label>
              <Input
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </div>
            <div>
              <Label>有効終了 (任意)</Label>
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
            <div>
              <Label>セット価格 (税抜)</Label>
              <Input type="number" value={excTax} onChange={(e) => setExcTax(Number(e.target.value))} />
            </div>
            <div>
              <Label>セット価格 (税込)</Label>
              <Input type="number" value={incTax} onChange={(e) => setIncTax(Number(e.target.value))} />
            </div>
          </div>
          <div>
            <Label>説明</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="!mb-0">構成 ({items.length})</Label>
              <Button type="button" variant="secondary" size="sm" onClick={addItem}>
                <Plus />
                品目追加
              </Button>
            </div>
            <div className="space-y-1.5">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select
                    value={it.productId}
                    onChange={(e) => updateItem(i, { productId: e.target.value })}
                    className="col-span-8 px-2 py-1.5 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
                  >
                    <option value="">— 選択 —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.sku})
                      </option>
                    ))}
                  </select>
                  <Input
                    className="col-span-3"
                    type="number"
                    min={1}
                    value={it.quantity}
                    onChange={(e) =>
                      updateItem(i, { quantity: Number(e.target.value) })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="col-span-1"
                    onClick={() => removeItem(i)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-xs text-[var(--text-muted)]">
                  品目を2件以上追加してください
                </p>
              )}
            </div>
            {/* 単品合計の参考表示 */}
            {items.length > 0 && (
              <p className="text-xs text-[var(--text-muted)] mt-1.5">
                参考: 単品合計 ¥
                {items
                  .reduce((sum, x) => {
                    const p = productMap.get(x.productId);
                    if (!p) return sum;
                    const price = (p.priceTable ?? []).find(
                      (e) => e.tag === 'general',
                    )?.priceIncludingTax ?? 0;
                    return sum + price * x.quantity;
                  }, 0)
                  .toLocaleString()}{' '}
                (税込)
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
